/**
 * WOOF Variant Swatches
 * Large gradient tiles for one variant option. Clicking a tile clicks the
 * matching radio inside the theme's own <variant-picker>, so all Horizon
 * variant behavior (price, media, buy button, URL) keeps working. The tiles
 * re-sync from the picker after every variant:update (the theme morphs the
 * picker's DOM on change).
 */
class WoofVariantSwatches extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-value]')];
    this.selectedEl = this.querySelector('[data-selected]');
    if (!this.buttons.length) return;

    this.buttons.forEach((button) => button.addEventListener('click', () => this.#select(button)));

    this.syncListener = () => this.#sync();
    document.addEventListener('variant:update', this.syncListener);
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.syncListener);
  }

  #radios() {
    const picker =
      document.querySelector(`variant-picker[data-section-id="${this.dataset.sectionId}"]`) ||
      document.querySelector('variant-picker');
    if (!picker) return [];

    const optionName = (this.dataset.optionName || '').trim().toLowerCase();
    const fieldsets = [...picker.querySelectorAll('fieldset')];
    const fieldset =
      fieldsets.find((candidate) =>
        candidate.querySelector('legend')?.textContent.trim().toLowerCase().startsWith(optionName)
      ) || fieldsets[0];
    return fieldset ? [...fieldset.querySelectorAll('input[type="radio"]')] : [];
  }

  #select(button) {
    this.#mark(button);
    const radio = this.#radios().find((candidate) => candidate.value === button.dataset.value);
    if (radio && !radio.checked) radio.click();
  }

  #mark(selected) {
    this.buttons.forEach((button) => {
      const isSelected = button === selected;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });
    if (this.selectedEl) this.selectedEl.textContent = selected.dataset.value;
  }

  #sync() {
    const checked = this.#radios().find((radio) => radio.checked);
    if (!checked) return;
    const button = this.buttons.find((candidate) => candidate.dataset.value === checked.value);
    if (button && !button.classList.contains('is-selected')) this.#mark(button);
  }
}

if (!customElements.get('woof-variant-swatches')) {
  customElements.define('woof-variant-swatches', WoofVariantSwatches);
}
